/*
 * FlightCarousel Component
 * Purpose: Hero carousel with product slides and CTA buttons
 * Uses global typography from index.css
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import styles from './FlightCarousel.module.css';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';

// Constants
const ASSETS = {
  image1: 'Assets/Images/home/image1.svg',
  image2: 'Assets/Images/home/image2.svg',
  image3: 'Assets/Images/home/image3.svg'
};

const SLIDES_DATA = [
  {
    id: 'ai-enabled-platform',
    image: ASSETS.image1,
    title: 'AI Enabled Drone Platform',
    buttonText: 'Buy'
  },
  {
    id: 'flight-communication',
    image: ASSETS.image2,
    title: 'Future Ready Flight Communication',
    buttonText: 'Buy'
  },
  {
    id: 'networked-solutions',
    image: ASSETS.image3,
    title: 'Networked Drone Solutions',
    buttonText: 'Buy'
  }
];

// Detect if device is mobile
const isMobile = () => {
  return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const FlightCarousel = () => {
  const navigate = useNavigate();
  const swiperRef = useRef(null);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobileDevice(isMobile());
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Enhanced Swiper configuration with mobile optimizations
  const SWIPER_CONFIG = useMemo(() => ({
    spaceBetween: 30,
    slidesPerView: 1,
    loop: true,
    grabCursor: true,
    touchRatio: 1,
    touchAngle: 45,
    threshold: 10,
    allowTouchMove: true,
    autoplay: {
      delay: isMobileDevice ? 4000 : 3000, // Slightly longer delay on mobile
      disableOnInteraction: false,
      pauseOnMouseEnter: !isMobileDevice, // Only pause on hover for desktop
      waitForTransition: true,
      stopOnLastSlide: false
    },
    pagination: {
      clickable: true,
      dynamicBullets: false
    },
    // Mobile-specific optimizations
    ...(isMobileDevice && {
      touchStartPreventDefault: false,
      touchMoveStopPropagation: false,
      simulateTouch: true,
      watchSlidesProgress: true,
      watchSlidesVisibility: true
    })
  }), [isMobileDevice]);

  // Memoized slides data
  const slidesData = useMemo(() => SLIDES_DATA, []);

  // Force restart autoplay when component becomes visible (helps with mobile)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (swiperRef.current && swiperRef.current.swiper) {
        if (document.hidden) {
          swiperRef.current.swiper.autoplay.stop();
        } else {
          swiperRef.current.swiper.autoplay.start();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Ensure autoplay starts after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (swiperRef.current && swiperRef.current.swiper) {
        swiperRef.current.swiper.autoplay.start();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Button hover handlers (only for desktop)
  const handleButtonHover = useCallback((slideId, isHovering) => {
    if (!isMobileDevice) {
      setHoveredButton(isHovering ? slideId : null);
    }
  }, [isMobileDevice]);

  // Button click handler
  const handleButtonClick = useCallback((slideId) => {
    navigate("/Resellers");
  }, [navigate]);

  // Handle swiper initialization
  const handleSwiperInit = useCallback((swiper) => {
    // Ensure autoplay starts immediately
    swiper.autoplay.start();
  }, []);

  return (
    <Swiper
      {...SWIPER_CONFIG}
      modules={[Pagination, Autoplay]}
      className={styles.carousel}
      ref={swiperRef}
      onSwiper={handleSwiperInit}
      onAutoplayTimeLeft={(swiper, time, progress) => {
        // Optional: Add visual progress indicator if needed
      }}
    >
      {slidesData.map((slide) => (
        <SwiperSlide key={slide.id}>
          <div className={styles.slideContainer}>
            <div className={styles.contentSection}>
              <div className={styles.titleWrapper}>
                {/* h1 will inherit styles from your global index.css */}
                <h1 className={styles.slideTitle}>{slide.title}</h1>
                
                <button
                  className={`${styles.ctaButton} ${
                    hoveredButton === slide.id ? styles.ctaButtonHovered : ''
                  }`}
                  onMouseEnter={() => handleButtonHover(slide.id, true)}
                  onMouseLeave={() => handleButtonHover(slide.id, false)}
                  onClick={() => handleButtonClick(slide.id)}
                  // Prevent button clicks from stopping autoplay on mobile
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  {slide.buttonText}
                </button>
              </div>
            </div>
            
            <div className={styles.imageSection}>
              <img 
                src={slide.image} 
                alt={slide.title}
                className={styles.slideImage}
                loading="lazy"
              />
            </div>
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default FlightCarousel;